// ─────────────────────────────────────────────────────────────────────────────
// CEO Command Center — pure four-state resolver (Task 10.2 / 10.4).
// Spec: executive-authority-rbac.
//
// Every authority data surface must render the mandatory states (Req 14.1–14.4):
//   • 'loading'      — skeleton/spinner while fetching
//   • 'error'        — retryable error that NEVER reveals privileged data
//   • 'empty'        — tier-appropriate empty state
//   • 'content'      — the resolved data
//   • 'not-enabled'  — honest state for an additive capability not yet shipped
//
// This resolver is the SINGLE source of truth for which state a query is in, so
// the `Gate` component and the four-state test agree exactly. Pure + total →
// unit/property-testable offline (Property 9, EA-9).
// ─────────────────────────────────────────────────────────────────────────────

export type GateState = 'loading' | 'error' | 'empty' | 'content' | 'not-enabled';

export interface GateQuery<T> {
  isLoading: boolean;
  isError: boolean;
  data?: T;
}

/**
 * Resolve the mandatory surface state from a query.
 *
 * Precedence (highest first): not-enabled → error → loading → empty → content.
 * `not-enabled` wins so an unshipped capability is never shown as a spinner or a
 * misleading error. `error` is preferred over `loading` so a failed refetch shows
 * the retry affordance rather than a perpetual skeleton. Emptiness treats
 * null/undefined and empty arrays as empty.
 *
 * @param q         the query (loading/error/data)
 * @param enabled   whether the capability is shipped (default true)
 */
export function resolveGateState<T>(q: GateQuery<T>, enabled = true): GateState {
  if (!enabled) return 'not-enabled';
  if (q.isError) return 'error';
  if (q.isLoading) return 'loading';
  const d = q.data;
  const isEmpty = d == null || (Array.isArray(d) && d.length === 0);
  return isEmpty ? 'empty' : 'content';
}
